import type { useNavigate } from '@tanstack/react-router';

import { launchQuickAction } from '@/features/quick-actions/launch-quick-action.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import { useChatSessionStore } from '@/integration/state/chat-session-store.js';

type NavigateFn = ReturnType<typeof useNavigate>;

/**
 * Opens a new chat and sends `draft`, so the agent starts at once. The one-shot launcher signals, any
 * composer prefill left by another flow and the active conversation are cleared first so `/chats`
 * mounts as a fresh chat with an empty composer; the draft is then
 * staged the way the Home launcher stages a submit, and the chat page sends it on mount. Only that
 * page reads a staged submit, so no composer still on screen, such as the Home one, can take it.
 *
 * `workspaceId` moves the chat into that workspace first. A surface that manages a workspace other
 * than the selected one passes it, or the agent would set up the wrong workspace.
 */
export function openQualityChat(args: {
  draft: string;
  navigate: NavigateFn;
  workspaceId?: string;
}): void {
  const { draft, navigate, workspaceId } = args;
  const ui = useUiStore.getState();
  if (workspaceId) ui.setSelectedWorkspaceId(workspaceId, 'user');
  ui.setPendingConversationId(null);
  ui.setPendingAttachments([]);
  ui.setPendingDraft(null);
  ui.setPendingFiles([]);
  ui.setPendingMic(false);
  ui.setComposerPrefill(null);
  ui.setComposerPrefillAttachments([]);
  ui.setComposerPrefillSkillIds([]);
  ui.setComposerPrefillRestoresDraft(false);
  const session = useChatSessionStore.getState();
  session.setActiveConversationId(null);
  session.setStreamingConvId(null);
  launchQuickAction({
    prompt: draft,
    skills: [],
    attachments: [],
    launch_mode: 'send',
  });
  void navigate({ to: '/chats' });
}

/** Where a guided-setup chat was started from; the agent reads it from the marker. */
export type SetupEntry =
  | 'home'
  | 'workspace-settings'
  | 'quality-header'
  | 'empty-state'
  | 'enable-banner'
  | 'badge'
  | 'tip';

/** The three outcomes guided setup can work towards. */
export type SetupGoal = 'generate' | 'tms' | 'exploratory';

/**
 * The link the agent recognizes as a guided-setup start. It points at `/quality` with no id, so the
 * run-card reference (`/quality/<id>?card=run`) never matches it.
 */
export function setupMarker(entry: SetupEntry, goal?: SetupGoal): string {
  const params = new URLSearchParams({ via: 'setup', entry });
  if (goal) params.set('goal', goal);
  return `[Quality setup](/quality?${params.toString()})`;
}

/** The English draft a guided-setup chat starts with: the request, then the marker on its own line. */
export function setupDraft(args: {
  entry: SetupEntry;
  goal?: SetupGoal;
  request?: string;
}): string {
  const request = args.request ?? 'Help me set up Quality for this workspace.';
  return `${request}\n\n${setupMarker(args.entry, args.goal)}`;
}

/** Opens a new chat and sends the guided-setup draft. */
export function openSetupInAgent(args: {
  entry: SetupEntry;
  navigate: NavigateFn;
  goal?: SetupGoal;
  request?: string;
  workspaceId?: string;
}): void {
  openQualityChat({
    draft: setupDraft(args),
    navigate: args.navigate,
    workspaceId: args.workspaceId,
  });
}
